/**
 * trial-card - Card component for displaying a trial event
 * Shows trial name, host club, location, dates, and organization badge
 */
import { BaseElement } from '../base-element';
import { formatDateRange } from '../../utils/date-format';
import type { TrialEventSummaryDto } from '../../api/types';

export class TrialCard extends BaseElement {
  private _trial: TrialEventSummaryDto | null = null;

  get trial(): TrialEventSummaryDto | null {
    return this._trial;
  }

  set trial(value: TrialEventSummaryDto | null) {
    this._trial = value;
    if (this.isConnected) {
      this.updateRender();
    }
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private getOrganizationBadgeText(): string {
    if (!this._trial?.formTemplate?.organizationCode) {
      return 'Trial';
    }
    return `${this._trial.formTemplate.organizationCode} Sanctioned`;
  }

  private getFormattedDateRange(): string {
    if (!this._trial?.startDate || !this._trial?.endDate) {
      return 'Date TBD';
    }
    return formatDateRange(this._trial.startDate, this._trial.endDate);
  }

  private getLocation(): string {
    if (!this._trial) return '';

    const city = this._trial.addressCity;
    const state = this._trial.addressState;

    if (city && state) {
      return `${city}, ${state}`;
    }

    // Fallback to any location field or empty
    return city || state || '';
  }

  private getRegisterUrl(): string {
    if (!this._trial?.trialEventId) return '#';
    return `/trials/${this._trial.trialEventId}/register`;
  }

  private handleRegisterClick(e: Event): void {
    e.preventDefault();
    const url = this.getRegisterUrl();
    this.emit('navigate', { path: url });
    // Also update browser history
    window.history.pushState({}, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  private updateRender(): void {
    if (!this._trial) {
      this.render(`
        <div class="empty text-center text-sm text-neutral-500">No trial data</div>
      `);
      return;
    }

    const badgeText = this.getOrganizationBadgeText();
    const dateRange = this.getFormattedDateRange();
    const location = this.getLocation();
    const trialName = this._trial.name || 'Untitled Trial';
    const hostClub = this._trial.hostClubName || 'Unknown Club';

    this.render(`
      <article class="card group flex h-full flex-col rounded-2xl border border-transparent bg-white p-6 shadow-xs transition-all duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-lg dark:bg-surface-dark dark:border-white/10" data-trial-id="${this.esc(this._trial.trialEventId)}">
        <div class="card-header mb-4 flex items-start justify-between">
          <span class="badge inline-flex items-center rounded-full border border-primary-200/60 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-600" data-testid="org-badge">${this.esc(badgeText)}</span>
        </div>

        <h3 class="title mb-1 text-lg font-bold text-neutral-900 transition-colors group-hover:text-primary-600 dark:text-white" data-testid="trial-name">${this.esc(trialName)}</h3>
        <p class="host-club mb-4 text-sm font-medium text-neutral-600 dark:text-neutral-400" data-testid="host-club">${this.esc(hostClub)}</p>

        <div class="details mb-6 flex flex-col gap-2">
          ${location ? `
          <div class="detail-row flex items-center text-sm text-neutral-600 dark:text-neutral-300" data-testid="location">
            <span class="detail-icon mr-2 inline-flex h-5 w-5 items-center justify-center text-primary-500">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4">
                <path fill-rule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd"/>
              </svg>
            </span>
            <span>${this.esc(location)}</span>
          </div>
          ` : ''}
          <div class="detail-row flex items-center text-sm text-neutral-600 dark:text-neutral-300" data-testid="date-range">
            <span class="detail-icon mr-2 inline-flex h-5 w-5 items-center justify-center text-primary-500">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4">
                <path d="M12.75 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM7.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM8.25 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM9.75 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM10.5 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM12.75 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM14.25 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 13.5a.75.75 0 100-1.5.75.75 0 000 1.5z"/>
                <path fill-rule="evenodd" d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z" clip-rule="evenodd"/>
              </svg>
            </span>
            <span>${this.esc(dateRange)}</span>
          </div>
        </div>

        <div class="card-footer mt-auto flex items-center justify-between border-t border-neutral-100 pt-4 dark:border-white/10">
          <span class="status-text text-xs font-medium text-neutral-500 dark:text-neutral-400">Registration open</span>
          <button class="register-btn inline-flex items-center gap-1 text-sm font-semibold text-primary-600 transition-colors duration-150 hover:text-primary-700" data-testid="register-btn">
            Register
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1">
              <path fill-rule="evenodd" d="M12.97 3.97a.75.75 0 011.06 0l7.5 7.5a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 11-1.06-1.06l6.22-6.22H3a.75.75 0 010-1.5h16.19l-6.22-6.22a.75.75 0 010-1.06z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </article>
    `);

    // Add event listener to register button
    const registerBtn = this.$('.register-btn');
    if (registerBtn) {
      registerBtn.addEventListener('click', (e) => this.handleRegisterClick(e));
    }
  }
}

customElements.define('trial-card', TrialCard);
